/**
 * Shared utility functions for combobox hooks
 */

import * as Sentry from '@sentry/browser'
import { hasProp, isPlainObject } from 'remeda'
import type { ViewHook } from '../../helpers/utils'

/**
 * Default timeout for LiveView event callbacks (30 seconds)
 */
export const DEFAULT_PUSH_EVENT_TIMEOUT_MS = 30000

export type OptionPageReply = Readonly<{
  result: readonly unknown[]
  size: number
}>

export const isOptionPageReply = (value: unknown): value is OptionPageReply =>
  isPlainObject(value) &&
  hasProp(value, 'result') &&
  Array.isArray(value.result) &&
  hasProp(value, 'size') &&
  typeof value.size === 'number' &&
  Number.isSafeInteger(value.size) &&
  value.size >= 0

// Recognizes lifecycle cancellation without hiding operational request failures.
export const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError'

export type ClientEventDetail = Readonly<{
  payload: Readonly<Record<string, unknown>>
  reply: (reply: unknown) => void
}>

// Accepts only the detail shape `clientEventHook` dispatches.
const isClientEventDetail = (value: unknown): value is ClientEventDetail =>
  isPlainObject(value) &&
  isPlainObject(value.payload) &&
  typeof value.reply === 'function'

// Reads the request a client-options combobox dispatched, if this event is one.
export const clientEventDetail = (
  event: Event
): ClientEventDetail | undefined =>
  event instanceof CustomEvent && isClientEventDetail(event.detail)
    ? event.detail
    : undefined

// Routes option-page events to a browser-side owner, such as a report hook,
// as a bubbling DOM event whose `detail.reply` answers like a LiveView reply.
export const clientEventHook = (el: HTMLElement): ViewHook => {
  const dispatch = (
    event: string,
    payload: Record<string, unknown>,
    onReply?: (reply: unknown, ref: number) => unknown
  ): boolean => {
    const detail: ClientEventDetail = {
      payload,
      reply: (reply) => onReply?.(reply, 0)
    }

    return el.dispatchEvent(new CustomEvent(event, { bubbles: true, detail }))
  }

  return {
    el,
    pushEvent: dispatch,
    pushEventTo: (_target, event, payload, onReply) =>
      dispatch(event, payload, onReply)
  }
}

/**
 * Push a Phoenix LiveView event with Promise-based callback and timeout protection
 * @param hook - The LiveView hook instance
 * @param event - Event name to push
 * @param payload - Event payload data
 * @param target - Optional phx-target selector
 * @param timeoutMs - Timeout in milliseconds (default: DEFAULT_PUSH_EVENT_TIMEOUT_MS)
 * @param signal - Optional lifecycle signal that cancels the callback and timeout
 * @returns Promise that resolves with the server reply or rejects on timeout/error
 */
export const pushEventWithCallback = (
  hook: ViewHook,
  event: string,
  payload: Record<string, unknown>,
  target?: string,
  timeoutMs = DEFAULT_PUSH_EVENT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<unknown> =>
  new Promise<unknown>((resolve, reject) => {
    let settled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const pushFn = target
      ? (cb: (reply: unknown) => void) =>
          hook.pushEventTo(target, event, payload, cb)
      : (cb: (reply: unknown) => void) => hook.pushEvent(event, payload, cb)

    const settle = (complete: () => void): void => {
      if (settled) {
        return
      }

      settled = true
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId)
      }
      signal?.removeEventListener('abort', handleAbort)
      complete()
    }

    const handleAbort = (): void => {
      const abortError = new Error(`LiveView push event "${event}" aborted`)
      abortError.name = 'AbortError'
      settle(() => reject(abortError))
    }

    if (signal?.aborted) {
      handleAbort()
      return
    }

    signal?.addEventListener('abort', handleAbort, { once: true })
    timeoutId = setTimeout(() => {
      const timeoutError = new Error(
        `LiveView push event "${event}" timed out after ${timeoutMs}ms`
      )
      timeoutError.name = 'LiveViewPushTimeout'

      settle(() => {
        Sentry.captureException(timeoutError, {
          tags: {
            component: 'combobox',
            operation: 'pushEventWithCallback',
            event
          },
          extra: { target, payload, timeoutMs }
        })
        reject(timeoutError)
      })
    }, timeoutMs)

    try {
      pushFn((reply) => {
        settle(() => resolve(reply))
      })
    } catch (error) {
      settle(() => reject(error))
    }
  })
